import axios, { CancelTokenSource } from "axios";
import { ActionTree } from "vuex";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { MultiselectItem } from "@/models/MultiselectItem";
import { TableSettings } from "@/models/TableSettings";
import { CampaignEditViewModel, CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignGridView, RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import { CampaignsStatisticsByDate } from "@/models/campaigns/CampaignsStatisticsByDate";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { CreatePhoneViewModel } from "@/models/phones/CreatePhoneViewModel";
import { CampaignsState } from "@/models/states/CampaignsState";
import { RootState } from "@/models/states/RootState";
import ConfigService from "@/services/ConfigService";
import ApiService, { CommonMultiselectPayload } from "@/services/api/ApiService";
import CampaignsApiService, { CampaignsPayload, GetCampaignEditViewModelPayload } from "@/services/api/CampaignsApiService";

const actions: ActionTree<CampaignsState, RootState> = {
  getCampaignEditViewModel({ commit }, payload: GetCampaignEditViewModelPayload): Promise<CampaignEditViewModel> {
    commit("setCampaignEditViewModel", null);
    return new Promise((resolve, reject) => {
      CampaignsApiService.getCampaignEditViewModel(payload.id, payload.createPhone)
        .then((data: CampaignEditViewModel) => {
          commit("setCampaignEditViewModel", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaigns({ commit, state }, payload: CampaignsPayload): Promise<DataSourceResult> {
    const updateStore: boolean = payload.pageSize !== ConfigService.maxIntValue;
    if (updateStore) {
      commit("setCampaignsLoading", true);
    }
    return new Promise((resolve, reject) => {
      let axiosSource: CancelTokenSource = axios.CancelToken.source();
      commit("setCampaignsCancelTokenSource", axiosSource);
      CampaignsApiService.getCampaigns(payload, axiosSource)
        .then((data: DataSourceResult) => {
          if (updateStore) {
            if (state.page > 1) {
              data.Data = state.campaigns.Data?.concat(data.Data);
            }
            commit("setCampaigns", data);
            commit("setCampaignsLoading", false);
            commit("setCampaignsCancelTokenSource", null);
          }
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignsPageData({ commit, state }): Promise<CampaignsPageViewModel> {
    commit("setCampaignsPageData", null);
    return new Promise((resolve, reject) => {
      CampaignsApiService.getCampaignsPageData()
        .then((data: CampaignsPageViewModel) => {
          data.IsMediaChannel = state.isMediaChannel; // saving between page reloads
          commit("setCampaignsPageData", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignsStatsByDate({ commit, state }, payload: CampaignsPayload): Promise<CampaignsStatisticsByDate[]> {
    commit("setCampaignsStatsByDate", null);
    return new Promise((resolve, reject) => {
      CampaignsApiService.getCampaignsStatsByDate(payload, state.campaignsCancelTokenSource)
        .then((data: CampaignsStatisticsByDate[]) => {
          commit("setCampaignsStatsByDate", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCampaignsWithStats({ commit, state }, payload: CampaignsPayload): Promise<CampaignGridView[]> {
    commit("setCampaignsWithStats", null);
    return new Promise((resolve, reject) => {
      CampaignsApiService.getCampaignsWithStats(payload, state.campaignsCancelTokenSource)
        .then((data: CampaignGridView[]) => {
          commit("setCampaignsWithStats", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getClients({ commit }): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getClients()
        .then((data: MultiselectItem[]) => {
          commit("setClients", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getProjects({ commit }, payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getProjects(payload)
        .then((data: MultiselectItem[]) => {
          commit("setProjects", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  logout({ commit }): void {
    commit("logout");
  },

  setCampaignEditSelectedProjectsIds({ commit }, campaignEditSelectedProjectsIds: number[]): void {
    commit("setCampaignEditSelectedProjectsIds", campaignEditSelectedProjectsIds);
  },

  setCampaignEditSelectedTagsIds({ commit }, campaignEditSelectedTagsIds: number[]): void {
    commit("setCampaignEditSelectedTagsIds", campaignEditSelectedTagsIds);
  },

  setCampaignEditViewModel({ commit }, campaignEditViewModel: CampaignEditViewModel): void {
    commit("setCampaignEditViewModel", campaignEditViewModel);
  },
  
  setCampaignsCancelTokenSource({ commit }, cancelTokenSource: CancelTokenSource) {
    commit("setCampaignsCancelTokenSource", cancelTokenSource);
  },

  setCampaignsPayload({ commit }, campaignsPayload: CampaignsPayload): void {
    commit("setCampaignsPayload", campaignsPayload);
  },

  setCampaignsStatsByDate({ commit }, campaignsStatsByDate: CampaignsStatisticsByDate[]): void {
    commit("setCampaignsStatsByDate", campaignsStatsByDate);
  },

  setCampaignsWithStats({ commit }, campaignsWithStats: CampaignGridView[]): void {
    commit("setCampaignsWithStats", campaignsWithStats);
  },

  setColumns({ commit }, columns: GridColumn[]): void {
    commit("setColumns", columns);
  },

  setColumnsFilters({ commit }, columnsFilters: FilterCollection): void {
    commit("setColumnsFilters", columnsFilters);
  },

  setCreatePhoneViewModel({ commit }, createPhoneViewModel: CreatePhoneViewModel): void {
    commit("setCreatePhoneViewModel", createPhoneViewModel);
  },

  setDatePeriod({ commit }, datePeriod: Date[]): void {
    commit("setDatePeriod", datePeriod);
  },

  setGraphsHiddenOnScroll({ commit }, graphsHiddenOnScroll: boolean): void {
    commit("setGraphsHiddenOnScroll", graphsHiddenOnScroll);
  },

  setIsMediaChannel({ commit }, isMediaChannel: boolean): void {
    commit("setIsMediaChannel", isMediaChannel);
  },

  setNeedToRefreshCampaignsGrid({ commit }, needToRefreshCampaignsGrid: RefreshGridMode): void {
    commit("setNeedToRefreshCampaignsGrid", needToRefreshCampaignsGrid);
  },

  setPage({ commit }, page: number): void {
    commit("setPage", page);
  },

  setSearchValue({ commit }, searchValue: string): void {
    commit("setSearchValue", searchValue);
  },

  setSelectedCampaign({ commit }, selectedCampaign?: CampaignGridView): void {
    commit("setSelectedCampaign", selectedCampaign);
  },

  setSelectedCampaignGridColumnsIds({ commit }, selectedCampaignGridColumnsIds: number[]): void {
    commit("setSelectedCampaignGridColumnsIds", selectedCampaignGridColumnsIds);
  },

  setSelectedCampaignId({ commit }, campaignClientId: number): void {
    commit("setSelectedCampaignId", campaignClientId);
  },

  setSelectedCampaignsIds({ commit }, selectedCampaignsIds: number[]): void {
    commit("setSelectedCampaignsIds", selectedCampaignsIds);
  },

  setSelectedCampaignStatus({ commit }, selectedCampaignStatus?: CampaignStatus | null): void {
    commit("setSelectedCampaignStatus", selectedCampaignStatus);
  },

  setSelectedChannelMetadataTypesIds({ commit }, selectedChannelMetadataTypesIds: number[]): void {
    commit("setSelectedChannelMetadataTypesIds", selectedChannelMetadataTypesIds);
  },

  setSelectedClientsIds({ commit }, selectedClientsIds: number[]): void {
    commit("setSelectedClientsIds", selectedClientsIds);
  },

  setSelectedProjectsIds({ commit }, selectedProjectsIds: number[]): void {
    commit("setSelectedProjectsIds", selectedProjectsIds);
  },

  setShowGraphs({ commit }, showGraphs: boolean): void {
    commit("setShowGraphs", showGraphs);
  },

  setSortDir({ commit }, sortDir: string): void {
    commit("setSortDir", sortDir);
  },

  setSortField({ commit }, sortField: string): void {
    commit("setSortField", sortField);
  },

  setTableSettings({ commit }, tableSettings: TableSettings[]): void {
    commit("setTableSettings", tableSettings);
  },
};
export default actions;
